import type { AxiosError, AxiosRequestConfig } from 'axios'
import { useLanguageService } from '@/composables/language'
import { useAuthStore } from '@store/auth'
import api, { axiosInstance } from '@api'
import type { CustomLogin, JWT, Register, TokenRefresh, UserDetails } from '@apiTypes'
import router from '@/router'
import storage from '@/utils/storage'

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
const accessTokenKey = 'accessToken'
const refreshTokenKey = 'refreshToken'
const accessToken = () => storage.getItem(accessTokenKey)
const refreshToken = () => storage.getItem(refreshTokenKey)
export const useAccessToken = useStorage<string>(accessTokenKey, null, storage)
useAccessToken.value = accessToken()
export const useRefreshToken = useStorage<string>(refreshTokenKey, null, storage)
useRefreshToken.value = refreshToken()
export const isUserLoggedIn = () => !!useAccessToken.value
export const hasRefreshToken = () => !!useRefreshToken.value

function tokenUserId(token: string | null): string | null {
  try {
    const payload = token?.split('.')[1]

    return payload ? String(JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).user_id) : null
  }
  catch {
    return null
  }
}

// Another tab signed out ('ended': the shared tokens are gone) or signed in as
// somebody else ('switched'). A refresh by another tab (same user) is no change.
export function onSessionChangedInOtherTab(callback: (change: 'ended' | 'switched') => void) {
  window.addEventListener('storage', event => {
    const { key, oldValue, newValue } = event as StorageEvent
    if (key !== accessTokenKey)
      return

    if (!newValue)
      callback('ended')
    else if (tokenUserId(oldValue) !== tokenUserId(newValue))
      callback('switched')
  })
}

// The tokens are shared by every tab of the app, and a refresh token works only
// once: refreshing rotates it and blacklists the old one. Two tabs refreshing
// with the same token would leave one of them with a 401 that logs out all of
// them, so refreshes take turns under a Web Lock (held across the tabs of the
// origin), and a tab whose token another tab has replaced meanwhile takes the
// stored tokens instead of refreshing again.
const refreshLockName = 'instacarrier-token-refresh'

// Without Web Locks (older browsers, plain-http hosts) two tabs can still refresh
// at once: the refused one waits this long for the tokens of the other one.
const unlockedRefreshGraceMs = 2000

// A tab waits this long for the lock, which a refresh hanging in another tab
// would hold on to, and then refreshes without it.
const refreshLockWaitMs = 20000
let refreshRequest: Promise<TokenRefresh> | null = null

interface StoredTokens {
  access: string | null
  refresh: string | null
}

const storedTokens = (): StoredTokens => ({ access: accessToken(), refresh: refreshToken() })

export function loginHelper(data: CustomLogin): Promise<UserDetails> {
  return new Promise((resolve, reject) => {
    api.login.authLoginCreate(data, { skipAuthToken: true })
      .then(r => {
        handleJWTResponse(r.data)
        resolve(r.data.user)
      })
      .catch(err => {
        reject(err)
      })
  })
}

/**
 * Refreshes the tokens, unless another tab has refreshed them since
 * `failedRequest` (the request refused with a 401) was sent: then this tab
 * takes the tokens of that tab. Parallel callers in a tab share one refresh.
 */
export function refreshTokenHelper(failedRequest?: AxiosRequestConfig): Promise<TokenRefresh> {
  if (!refreshRequest) {
    const failedAccess = sentAccessToken(failedRequest) ?? useAccessToken.value

    refreshRequest = withRefreshLock(locked => refreshOrTakeOver(failedAccess, locked)).finally(() => {
      refreshRequest = null
    })
  }

  return refreshRequest
}

async function refreshOrTakeOver(failedAccess: string | null, locked: boolean): Promise<TokenRefresh> {
  const stored = storedTokens()

  // Signed out meanwhile, or refreshed since the request was sent (by another
  // tab, or by an earlier refresh of this one).
  if (!stored.refresh || (stored.access && stored.access !== failedAccess))
    return takeStoredTokens(stored)

  console.log('[AXIOS] Response Interceptors - Refreshing token')

  try {
    // Without skipLogout the interceptor would log out on a refused token,
    // before this tab could see whether another tab has replaced it.
    const { data } = await api.token.authTokenRefreshCreate(
      { refresh: stored.refresh, access: stored.access || '' },
      { skipAuthRefresh: true, skipLogout: true },
    )

    handleTokens(data)

    return data
  }
  catch (error) {
    if ((error as AxiosError).response?.status !== 401)
      throw error

    // Another tab may have used the same token a moment before (a stale read
    // of localStorage, or no lock at all): its new tokens will do. Only a token
    // nobody has replaced means the session is over.
    const replaced = locked
      ? tokensReplacing(stored.refresh)
      : await waitForTokensReplacing(stored.refresh, unlockedRefreshGraceMs)

    if (!replaced)
      throw error

    return takeStoredTokens(replaced)
  }
}

// Puts this tab on the stored tokens; fails when they are gone (signed out).
function takeStoredTokens(tokens: StoredTokens): TokenRefresh {
  useAccessToken.value = tokens.access
  useRefreshToken.value = tokens.refresh

  if (!tokens.access || !tokens.refresh)
    throw new Error('Signed out in another tab')

  console.log('[AXIOS] Response Interceptors - Token already refreshed')

  return { access: tokens.access, refresh: tokens.refresh }
}

// The stored tokens, if their refresh token is no longer `refused`.
function tokensReplacing(refused: string): StoredTokens | null {
  const tokens = storedTokens()

  return tokens.refresh === refused ? null : tokens
}

// The same, waiting up to `ms` for another tab to store them.
function waitForTokensReplacing(refused: string, ms: number): Promise<StoredTokens | null> {
  return new Promise(resolve => {
    let timeout: ReturnType<typeof setTimeout> | undefined

    const check = () => {
      const tokens = tokensReplacing(refused)

      if (!tokens)
        return

      clearTimeout(timeout)
      window.removeEventListener('storage', check)
      resolve(tokens)
    }

    window.addEventListener('storage', check)
    timeout = setTimeout(() => {
      window.removeEventListener('storage', check)
      resolve(null)
    }, ms)
    check()
  })
}

// Runs `refresh` holding the refresh lock of the origin. Where the browser has
// no Web Locks, or refuses the lock (e.g. a sandboxed frame), it runs at once,
// and without the lock once the wait for it has taken too long.
function withRefreshLock<T>(refresh: (locked: boolean) => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks

  if (!locks)
    return refresh(false)

  let granted = false
  const waiting = new AbortController()
  const giveUp = setTimeout(() => waiting.abort(), refreshLockWaitMs)

  return locks.request(refreshLockName, { signal: waiting.signal }, () => {
    granted = true
    clearTimeout(giveUp)

    return refresh(true)
  }).catch(error => {
    if (granted)
      throw error

    return refresh(false)
  }).finally(() => clearTimeout(giveUp))
}

// Registration does not sign in: e-mail verification is mandatory, so the response is a
// {detail} message, not a JWT pair.
export function registerHelper(data: Register): Promise<void> {
  return new Promise((resolve, reject) => {
    api.register.authRegisterCreate(data, { skipAuthToken: true })
      .then(() => resolve())
      .catch(err => {
        reject(err)
      })
  })
}

// Signs in with a JWT pair another endpoint issued (accepting a company invitation): keeps the
// pair as a login does and loads the user it belongs to.
export async function signInWithTokens(tokens: { access: string; refresh: string }) {
  handleTokens(tokens)
  await useAuthStore().fetchCurrentUser()
}

function handleJWTResponse(data: JWT) {
  const { access, refresh, user } = data

  const authStore = useAuthStore()

  authStore.setCurrentUser(user)
  handleTokens({ access, refresh })
}

function handleTokens(data: { refresh?: string; access?: string }) {
  // Stored at once, not on Vue's next flush, so that the tab taking the refresh
  // lock next reads them.
  if (data.access) {
    storage.setItem(accessTokenKey, data.access)
    useAccessToken.value = data.access
  }

  if (data.refresh) {
    storage.setItem(refreshTokenKey, data.refresh)
    useRefreshToken.value = data.refresh
  }
}

export function logoutHelper() {
  axiosInstance.post(
    '/api/auth/logout/',
    { refresh: refreshToken() },
    { skipAuthToken: true, skipAuthRefresh: true, skipLogout: true },
  ).catch(() => undefined)

  clearAuthData()
  router.push({ name: 'index' })
}

export function clearAuthDataAndRedirect() {
  clearAuthData()
  router.push({ name: 'login' })
}

export function clearAuthData() {
  // Removed at once too, not on Vue's next flush: an error in that flush (a page
  // rendering without its user) would otherwise leave them stored.
  storage.removeItem(accessTokenKey)
  storage.removeItem(refreshTokenKey)
  useAccessToken.value = null
  useRefreshToken.value = null

  const authStore = useAuthStore()

  authStore.clear()
}

export function setHeaders(config: AxiosRequestConfig) {
  const languageService = useLanguageService()
  const currentLang = languageService.currentLanguage.value

  config.headers = config.headers || {}

  config.headers['Accept-Language'] = currentLang || 'en'

  const token = useAccessToken.value

  if (!token)
    return // Exit early if there's no token

  // Type assertion to indicate that headers are guaranteed to be defined
  const headers = config.headers!

  if (!config.skipAuthToken) {
    // ** eslint-disable-next-line no-param-reassign
    headers.Authorization = `Bearer ${token}`
  }
}

// The access token setHeaders put on a request.
function sentAccessToken(config?: AxiosRequestConfig): string | undefined {
  const authorization = config?.headers?.Authorization

  return typeof authorization === 'string' ? authorization.replace(/^Bearer /, '') : undefined
}
